// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { PoolDynamic } from '@typechain-types/contracts'
import { Contract } from 'ethers'
import { PoolDynamicAbi } from './Abis'
import { DefiCollateralCTO, DefiType } from './CollateralSetCTO'
import { Erc20Wei } from './conversions'
import { DefiData } from './defiCTO'
import { TimeStructure } from './defiVaultUtils'
import { chainConfigurationToProvider, findChainConfigurationByChainId } from './DeploymentConfigs'
import { ChainId, EXCHANGE_RATE_ONE } from './Enums'
import { logOT } from './loggingShared'

export async function getSimulatedNameAndSymbol(
  collateralCTO: DefiCollateralCTO
): Promise<{ name: string; symbol: string }> {
  const { defiVaultAddr } = collateralCTO

  const chainConfiguration = findChainConfigurationByChainId(collateralCTO.defiChainId)

  const provider = chainConfigurationToProvider(chainConfiguration)
  const defiContract = new Contract(defiVaultAddr, PoolDynamicAbi, provider) as unknown as PoolDynamic
  const name = await defiContract.name()
  const symbol = await defiContract.symbol()
  return { name, symbol }
}

export async function getDefiDataSimulated(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<DefiData> {
  const { defiVaultAddr, transferWalletAddr, calibrationWalletAddr } = collateralCTO

  const chainConfiguration = findChainConfigurationByChainId(collateralCTO.defiChainId)

  const provider = chainConfigurationToProvider(chainConfiguration)
  logOT.FOCUS('FOCUS: getDefiDataSimulated 1', defiVaultAddr)
  let blockTag
  if (!timeStructure) {
    blockTag = 'latest'
  } else {
    if (chainConfiguration.chainId === ChainId.MAINNET) {
      blockTag = timeStructure.blockNumberEthereum
    } else if (chainConfiguration.chainId === ChainId.AVALANCHE) {
      blockTag = timeStructure.blockNumberAvax
    } else {
      throw new Error(`Unsupported chain id: ${chainConfiguration.chainId}`)
    }
  }
  const defiContract = new Contract(defiVaultAddr, PoolDynamicAbi, provider) as unknown as PoolDynamic

  const defiOTShares: bigint = await defiContract.balanceOf(transferWalletAddr, { blockTag })

  const defiOTAssets: bigint = await defiContract.convertToAssets(defiOTShares, { blockTag })
  logOT.FOCUS('ASSETS', {
    defiOTAssets,
    defiOTShares,
    defiVaultAddr,
    transferWalletAddr,
    calibrationWalletAddr,
  })

  const calibrationShares: bigint = await defiContract.balanceOf(calibrationWalletAddr, { blockTag })
  const calibrationAssets: bigint = await defiContract.convertToAssets(calibrationShares, { blockTag })

  const defiTotalShares: bigint = await defiContract.totalSupply({ blockTag })
  const defiTotalAssets: bigint = await defiContract.totalAssets({ blockTag })

  const nativeDefiExchangeRateUnscaled: bigint = await defiContract.exchangeRate({ blockTag })
  const nativeDefiExchangeRateBeforeFees: bigint =
    (nativeDefiExchangeRateUnscaled * BigInt(collateralCTO.nativeDefiExchangeRateScale)) / EXCHANGE_RATE_ONE
  const decimals: bigint = await defiContract.decimals({ blockTag })

  const annualInterestBips = 10 * 10000
  logOT.info('annualInterestBips', annualInterestBips)

  const retValue: DefiData = {
    defiType: DefiType.SIMULATED,

    defiTotalShares: Erc20Wei(defiTotalShares), //NA
    defiTotalAssets: Erc20Wei(defiTotalAssets), //NA
    defiOTShares: Erc20Wei(defiOTShares),
    defiOTAssets: Erc20Wei(defiOTAssets),
    interestRate: Number(annualInterestBips) / 10000,

    decimals: Number(decimals),
    calibratedBalance: Erc20Wei(calibrationAssets),
    calibratedExchangeRateBeforeFees: Erc20Wei((calibrationAssets * EXCHANGE_RATE_ONE) / 1000n / 10n ** decimals),

    nativeDefiExchangeRateBeforeFees: Erc20Wei(nativeDefiExchangeRateBeforeFees),
    collateralCTO: collateralCTO,
  }
  logOT.info(getDefiDataSimulated.name, { collateralCTO, retValue })
  return retValue
}
